import { useState, useEffect, useRef, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Search, Building2, MapPin, Package, Loader2, X } from "lucide-react";
import { Input } from "@/shared/ui/input";
import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { fetchAuthorizedProperties } from "@/features/projects/services/projectsApi";
import { buildPropertySearchFilterForToken, getPropertySearchTokens } from "@/features/properties/services/propertySearch";

interface SearchResult {
  id: string;
  name: string;
  address: string;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  store_number: string | null;
  climate_zone: string | null;
  property_type: string | null;
  asset_count: number;
}

interface LivePropertySearchProps {
  clientId: string;
  value?: string;
  onPropertySelect?: (propertyId: string) => void;
  onSearchTermChange?: (term: string) => void;
  showDropdown?: boolean;
  placeholder?: string;
  className?: string;
  /** Shorter, denser input — for use inside toolbars/filter bars. */
  compact?: boolean;
}

interface DropdownPosition {
  top: number;
  left: number;
  width: number;
}

export function LivePropertySearch({ 
  clientId, 
  value,
  onPropertySelect,
  onSearchTermChange,
  showDropdown = true,
  placeholder = "Search properties by name, address, or store number...",
  className,
  compact = false,
}: LivePropertySearchProps) {
  const navigate = useNavigate();
  const [internalSearchTerm, setInternalSearchTerm] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition>({ top: 0, left: 0, width: 0 });
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout>();
  const searchTerm = value ?? internalSearchTerm;

  const updateSearchTerm = (term: string) => {
    if (value === undefined) {
      setInternalSearchTerm(term);
    }
    onSearchTermChange?.(term);
  };

  // Calculate dropdown position
  const updateDropdownPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8, // 8px gap
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Update position on open and scroll/resize
  useLayoutEffect(() => {
    if (isOpen) {
      updateDropdownPosition();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => {
      updateDropdownPosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!searchTerm.trim()) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    if (!showDropdown) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const searchTokens = getPropertySearchTokens(searchTerm);
        if (searchTokens.length === 0) {
          setResults([]);
          setIsOpen(false);
          return;
        }

        // Search the authorized properties (org-scoped by the /projects/client
        // endpoint; loaded from Supabase by ref_id). Search filters/limit run
        // per ref_id chunk, then merged + capped to 10.
        const data = (
          await fetchAuthorizedProperties<any>(
            `
            id,
            name,
            address,
            city,
            state,
            zip_code,
            country,
            store_number,
            climate_zone,
            property_type,
            property_assets(count)
          `,
            (q) => {
              for (const token of searchTokens) {
                q = q.or(buildPropertySearchFilterForToken(token));
              }
              return q.order('name', { ascending: true }).limit(10);
            },
          )
        )
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''))
          .slice(0, 10);

        const transformedResults: SearchResult[] = (data || []).map(prop => ({
          id: prop.id,
          name: prop.name,
          address: prop.address,
          city: prop.city,
          state: prop.state,
          zip_code: prop.zip_code,
          country: prop.country,
          store_number: prop.store_number,
          climate_zone: prop.climate_zone,
          property_type: prop.property_type,
          asset_count: prop.property_assets?.[0]?.count || 0,
        }));

        setResults(transformedResults);
        setIsOpen(transformedResults.length > 0);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 200); // 200ms debounce for snappy feel

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, clientId, showDropdown]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < results.length) {
          handleSelect(results[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleSelect = (property: SearchResult) => {
    if (onPropertySelect) {
      onPropertySelect(property.id);
    } else {
      navigate(`/property/${property.id}`);
    }
    updateSearchTerm("");
    setIsOpen(false);
    setResults([]);
  };

  const clearSearch = () => {
    updateSearchTerm("");
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const formatLocation = (property: SearchResult) => {
    const cityState = [property.city, property.state].filter(Boolean).join(", ");
    return [property.address, cityState, property.zip_code].filter(Boolean).join(", ");
  };

  // Highlight matching text
  const highlightMatch = (text: string, query: string) => {
    if (!query.trim()) return text;
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);
    return parts.map((part, i) => 
      regex.test(part) ? (
        <span key={i} className="bg-primary/30 text-primary font-semibold rounded px-0.5">
          {part}
        </span>
      ) : part
    );
  };

  // Render dropdown via Portal
  const renderDropdown = () => {
    if (!isOpen) return null;

    const dropdownContent = results.length > 0 ? (
      <div
        ref={dropdownRef}
        style={{
          position: 'absolute',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 9999,
        }}
        className="bg-popover border border-border rounded-xl shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95 slide-in-from-top-2"
      >
        <div className="p-2 border-b border-border/50 bg-muted/30">
          <p className="text-xs text-muted-foreground px-2">
            Found <span className="font-semibold text-foreground">{results.length}</span> properties
          </p>
        </div>
        <div className="max-h-[400px] overflow-y-auto">
          {results.map((property, index) => (
            <button
              key={property.id}
              onClick={() => handleSelect(property)}
              className={cn(
                "w-full px-4 py-3 flex items-start gap-4 text-left transition-all duration-200",
                "hover:bg-primary/10 border-b border-border/30 last:border-0",
                selectedIndex === index && "bg-primary/15"
              )}
            >
              {/* Property Icon */}
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-primary flex items-center justify-center shadow-sm">
                <Building2 className="h-5 w-5 text-primary-foreground" />
              </div>

              {/* Property Info */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-foreground truncate mb-1">
                  {highlightMatch(property.name, searchTerm)}
                </h4>
                <p className="text-sm text-muted-foreground flex items-center gap-1.5 truncate">
                  <MapPin className="h-3.5 w-3.5 flex-shrink-0 text-primary/70" />
                  {highlightMatch(formatLocation(property) || "No address recorded", searchTerm)}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-1.5">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Package className="h-3 w-3" />
                    {property.asset_count} assets
                  </span>
                  {property.store_number && (
                    <Badge variant="outline" className="text-xs">
                      Store #{highlightMatch(property.store_number, searchTerm)}
                    </Badge>
                  )}
                  {(property.city || property.state) && (
                    <Badge variant="secondary" className="text-xs">
                      {highlightMatch([property.city, property.state].filter(Boolean).join(", "), searchTerm)}
                    </Badge>
                  )}
                  {property.zip_code && (
                    <Badge variant="secondary" className="text-xs">
                      ZIP {highlightMatch(property.zip_code, searchTerm)}
                    </Badge>
                  )}
                  {property.climate_zone && (
                    <Badge variant="secondary" className="text-xs">
                      Climate {highlightMatch(property.climate_zone, searchTerm)}
                    </Badge>
                  )}
                  {property.property_type && (
                    <Badge variant="secondary" className="text-xs">
                      {property.property_type}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Arrow indicator */}
              <div className="flex-shrink-0 self-center">
                <div className={cn(
                  "w-8 h-8 rounded-lg flex items-center justify-center transition-all",
                  selectedIndex === index ? "bg-primary text-primary-foreground" : "bg-muted/50"
                )}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </button>
          ))}
        </div>
        
        {/* Keyboard hints */}
        <div className="p-2 border-t border-border/50 bg-muted/30 flex items-center justify-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">↑↓</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Enter</kbd>
            Select
          </span>
          <span className="flex items-center gap-1">
            <kbd className="px-1.5 py-0.5 bg-muted rounded text-[10px] font-mono">Esc</kbd>
            Close
          </span>
        </div>
      </div>
    ) : searchTerm && !isLoading ? (
      <div
        ref={dropdownRef}
        style={{
          position: 'absolute',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 9999,
        }}
        className="bg-popover border border-border rounded-xl shadow-2xl overflow-hidden animate-in fade-in-0 zoom-in-95"
      >
        <div className="p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
            <Search className="h-5 w-5 text-muted-foreground" />
          </div>
          <p className="text-sm text-muted-foreground">
            No properties found for "<span className="font-medium text-foreground">{searchTerm}</span>"
          </p>
        </div>
      </div>
    ) : null;

    if (!dropdownContent) return null;

    return createPortal(dropdownContent, document.body);
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Search Input */}
      <div className="relative">
        <Search className={cn(
          "absolute top-1/2 transform -translate-y-1/2 text-muted-foreground",
          compact ? "left-2.5 h-4 w-4" : "left-4 h-5 w-5"
        )} />
        <Input
          ref={inputRef}
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => {
            updateSearchTerm(e.target.value);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => showDropdown && results.length > 0 && setIsOpen(true)}
          className={cn(
            "bg-card/50 border-border/50 transition-all",
            compact ? "pl-8 pr-8 h-8 text-sm" : "pl-12 pr-12 h-12"
          )}
        />
        {isLoading && (
          <Loader2 className={cn(
            "absolute top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-primary",
            compact ? "right-8" : "right-12"
          )} />
        )}
        {searchTerm && !isLoading && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearSearch}
            className={cn(
              "absolute top-1/2 transform -translate-y-1/2 p-0 hover:bg-muted",
              compact ? "right-1 h-6 w-6" : "right-2 h-8 w-8"
            )}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </Button>
        )}
      </div>

      {/* Portal-based dropdown */}
      {renderDropdown()}
    </div>
  );
}
